import styled from "@emotion/styled";
import { brush } from "d3";
import { select as d3Select } from "d3-selection";
import { zoom, zoomIdentity } from "d3-zoom";
import * as React from "react";
import { useEffect, useLayoutEffect, useMemo } from "react";
import { useMeasure, usePrevious } from "react-use";

import Transform from "./transform";

const mapUrl = (uuid: string) => `https://maps.griptonite.io/${uuid}.svg`;

const Wrapper = styled.div`
  height: 100%;
  width: 100%;
  position: relative;
  overflow: hidden;
  touch-action: none;
`;

type MapData = {
  uuid: string;
  width: number;
  height: number;
};

type Dimensions = {
  width: number;
  height: number;
  screenWidth: number;
  screenHeight: number;
};

interface MapObject {
  mapPosition: { x: number; y: number };
}

type Props<TMapObject> = {
  map: MapData;
  zoomToBbox: boolean;
  maxZoom: number;
  mapObjects: TMapObject[];
  bboxPadding?: number | number[];
  renderMapObject: (
    mapObject: TMapObject,
    transform: Transform,
    isSelected: boolean,
  ) => React.ReactNode;
  idAccessor: (x: TMapObject) => string;
  selectedIds: Set<string>;
  onSetSelectedIds: (ids: string[]) => void;
  onZoomChange?: () => void;
  zoomState?: {
    center: { x: number; y: number };
    scale: number;
  };
  onContextMenu?: (e: React.MouseEvent<SVGSVGElement>, x: number, y: number) => void;
  onBrushStart?: () => void;
  brushEnabled?: boolean;
};
const MapObjects = <TMapObject extends MapObject = MapObject>({
  transform,
  mapObjects,
  renderMapObject,
  idAccessor,
  selectedIds,
}: {
  transform: Transform;
  // oxlint-disable-next-line typescript/no-explicit-any
  mapObjects: TMapObject[];
  dimensions: Dimensions;
  idAccessor: (x: TMapObject) => string;
  selectedIds: Set<string>;
  renderMapObject: (
    mapObject: TMapObject,
    transform: Transform,
    isSelected: boolean,
  ) => React.ReactNode;
}) => {
  const idAccessorRef = React.useRef(idAccessor);
  React.useEffect(() => {
    idAccessorRef.current = idAccessor;
  }, [idAccessor]);
  const sortedByIsSelected = useMemo(() => {
    const m = mapObjects.slice();
    m.sort((a, b) => {
      const aSelected = selectedIds.has(idAccessorRef.current(a));
      const bSelected = selectedIds.has(idAccessorRef.current(b));
      if (aSelected && !bSelected) {
        return 1;
      }
      if (!aSelected && bSelected) {
        return -1;
      }
      return 0;
    });
    return m;
  }, [mapObjects, selectedIds]);
  return (
    <>
      {sortedByIsSelected.map((x) => renderMapObject(x, transform, selectedIds.has(idAccessor(x))))}
    </>
  );
};

const getBbox = (mapObjects: MapObject[], padding = 10) => {
  let y1 = Infinity;
  let y2 = -Infinity;
  let x1 = Infinity;
  let x2 = -Infinity;
  mapObjects.forEach((mapObject) => {
    y1 = Math.min(mapObject.mapPosition.y, y1);
    y2 = Math.max(mapObject.mapPosition.y, y2);
    x1 = Math.min(mapObject.mapPosition.x, x1);
    x2 = Math.max(mapObject.mapPosition.x, x2);
  });
  return {
    x1: x1 - padding,
    x2: x2 + padding,
    y1: y1 - padding,
    y2: y2 + padding,
  };
};

// const MapPinCollection: React.FC = ({children}) => {
//   return <div className="pins">{children}</div>;
// };

const Map = <TMapObject extends MapObject = MapObject>({
  map,
  mapObjects,
  selectedIds,
  onSetSelectedIds,
  idAccessor,
  zoomToBbox = true,
  bboxPadding = 30,
  zoomState,
  renderMapObject,
  onZoomChange,
  onContextMenu,
  onBrushStart,
  brushEnabled = true,
}: Props<TMapObject>) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const brushElRef = React.useRef<SVGGElement>(null);
  const svgRef = React.useRef<SVGSVGElement>(null);
  const [measureRef, { width, height }] = useMeasure();
  useLayoutEffect(() => {
    ref.current && measureRef(ref.current);
  }, [measureRef]);

  const [userTransform, setUserTransform] = React.useState(new Transform());

  const bbox = React.useMemo(() => getBbox(mapObjects, 0), [mapObjects]);

  // const bboxH = bbox.y2 - bbox.y1;
  // const bboxW = bbox.x2 - bbox.x1;

  let zoomBbox1: [number, number], zoomBbox2: [number, number];
  if (zoomToBbox) {
    zoomBbox1 = [bbox.x1, bbox.y1];
    zoomBbox2 = [bbox.x2, bbox.y2];
  } else {
    zoomBbox1 = [0, 0];
    zoomBbox2 = [map.width, map.height];
  }

  const zoomBboxH = zoomBbox2[1] - zoomBbox1[1];
  const zoomBboxW = zoomBbox2[0] - zoomBbox1[0];

  // let scaleFactor = height / map.height;
  // if (scaleFactor * map.width >= width) {
  //   scaleFactor = width / map.width;
  // }
  // let translateX = width / 2 - (map.width * scaleFactor) / 2;
  // let translateY = height / 2 - (map.height * scaleFactor) / 2;
  // const coords = [136.83553222656252, 83.28556618239614];
  // const maxScaleFactor =
  //   Math.min(
  //     (height - bboxPadding * 2) / map.height,
  //     (width - bboxPadding * 2) / map.width
  //   ) * maxZoom;
  if (typeof bboxPadding === "number") {
    // T, R, B, L
    bboxPadding = [bboxPadding, bboxPadding, bboxPadding, bboxPadding];
  }
  const scaleFactor = Math.min(
    (height - bboxPadding[0] - bboxPadding[2]) / zoomBboxH,
    (width - bboxPadding[1] - bboxPadding[3]) / zoomBboxW,
  );
  const baseTransform = React.useMemo(
    () =>
      new Transform(
        scaleFactor,
        width / 2 + bboxPadding[1] - bboxPadding[3] - (zoomBbox1[0] + zoomBboxW / 2) * scaleFactor,
        height / 2 + bboxPadding[0] - bboxPadding[2] - (zoomBbox1[1] + zoomBboxH / 2) * scaleFactor,
      ),
    [width, height, zoomBbox1, zoomBboxW, zoomBboxH, scaleFactor],
  );

  const zoomRef = React.useRef(
    zoom()
      .scaleExtent([1, 8])
      .translateExtent([
        [0, 0],
        [map.width, map.height],
      ])
      .clickDistance(5)
      .on("zoom", (event) => {
        setUserTransform(new Transform(event.transform.k, event.transform.x, event.transform.y));

        if (event.sourceEvent) {
          onZoomChange?.();
        }
      }),
  );

  useEffect(() => {
    if (zoomState && svgRef.current && ref.current) {
      setTimeout(() => {
        const xy = baseTransform.apply([zoomState.center.x, zoomState.center.y]);
        d3Select(svgRef.current as Element)
          .transition()
          .duration(250)
          .call(
            zoomRef.current.transform,
            zoomIdentity
              .translate(width / 2, height / 2)
              .scale(zoomState.scale)
              .translate(-xy[0], -xy[1]),
          );
      }, 100);
    } else {
      setUserTransform(new Transform());
    }
  }, [zoomState]);

  const finalTransform = React.useMemo(() => {
    return userTransform.compose(baseTransform);
  }, [baseTransform, userTransform]);

  const handleBrushSelection = React.useCallback(
    (extents: [[number, number], [number, number]]) => {
      if (!brushRef.current) {
        return;
      }

      if (!extents) {
        onSetSelectedIds([]);
        return;
      }

      const transformedExtents = extents.map((x) => finalTransform.invert(x));

      const selection = mapObjects.filter((x) => {
        const p = x.mapPosition;
        return (
          p.x >= transformedExtents[0][0] &&
          p.x <= transformedExtents[1][0] &&
          p.y >= transformedExtents[0][1] &&
          p.y <= transformedExtents[1][1]
        );
      });
      onSetSelectedIds(selection.map(idAccessor));
    },
    [finalTransform, onSetSelectedIds],
  );

  const handleBrushSelectionRef = React.useRef(handleBrushSelection);
  useEffect(() => {
    handleBrushSelectionRef.current = handleBrushSelection;
  }, [handleBrushSelection]);

  const brushRef = React.useRef(
    brush()
      .on("end", (event) => {
        if (event.sourceEvent?.type) {
          console.log("type", event.sourceEvent.type);
          handleBrushSelectionRef.current(event.selection);
          brushElRef.current && d3Select(brushElRef.current).call(brushRef.current.clear);
        }
        // setTimeout(() => {
        // }, 0);
      })
      .on("start", () => onBrushStart?.()),
  );

  React.useEffect(() => {
    if (ref.current && svgRef.current) {
      const z = d3Select(svgRef.current as Element).call(zoomRef.current);
      if (brushEnabled) {
        z.on("mousedown.zoom", null)
          .on("touchstart.zoom", null)
          .on("touchmove.zoom", null)
          .on("touchend.zoom", null);
      }
    }
  }, [brushEnabled]);

  const prevWidth = usePrevious(width);
  const prevHeight = usePrevious(height);
  const userTransformRef = React.useRef(userTransform);
  useLayoutEffect(() => {
    userTransformRef.current = userTransform;
  }, [userTransform]);
  React.useEffect(() => {
    zoomRef.current.translateExtent([
      [0, 0],
      [width, height],
    ]);
    if (brushRef.current && brushElRef.current) {
      if (brushEnabled) {
        d3Select(brushElRef.current).call(brushRef.current);
      } else {
        d3Select(brushElRef.current).selectChildren().remove();
      }
      brushRef.current.keyModifiers(false);
    }
    if (prevHeight && prevWidth) {
      const dx = width - prevWidth;
      const dy = height - prevHeight;
      if (ref.current && svgRef.current) {
        d3Select(svgRef.current as Element).call(
          zoomRef.current.transform,
          new Transform(
            userTransformRef.current.k,
            userTransformRef.current.x - ((userTransformRef.current.k - 1) * dx) / 2,
            userTransformRef.current.y - ((userTransformRef.current.k - 1) * dy) / 2,
          ),
        );
      }
    }
  }, [prevWidth, prevHeight, width, height, brushEnabled]);

  const handleContextMenu = (e: React.MouseEvent<SVGSVGElement>) => {
    if (onContextMenu) {
      const rect = svgRef.current?.getBoundingClientRect();
      if (!rect) {
        return;
      }
      const mapScreenCoords = [e.clientX - rect.x, e.clientY - rect.y] as [number, number];
      const [x, y] = finalTransform.invert(mapScreenCoords);
      onContextMenu(e, x, y);
    }
  };

  return (
    <Wrapper
      ref={ref}
      className="map-container"
      data-uuid={map.uuid}
      data-transform={JSON.stringify([finalTransform.k, finalTransform.x, finalTransform.y])}
    >
      <>
        <svg style={{ width: "100%", height: "100%", cursor: "grab" }} ref={svgRef}>
          <g
            transform={`translate(${finalTransform.x}, ${finalTransform.y}) scale(${finalTransform.k})`}
          >
            <image x="0" y="0" width={map.width} height={map.height} href={mapUrl(map.uuid)} />
          </g>
          <g className="brush" onContextMenu={handleContextMenu} ref={brushElRef}></g>
          <g
            transform={`translate(${finalTransform.x}, ${finalTransform.y}) scale(${finalTransform.k})`}
          >
            <MapObjects
              selectedIds={selectedIds}
              renderMapObject={renderMapObject}
              transform={finalTransform}
              mapObjects={mapObjects}
              idAccessor={idAccessor}
              dimensions={{
                screenWidth: width,
                screenHeight: height,
                width: map.width,
                height: map.height,
              }}
            />
          </g>
        </svg>
      </>
    </Wrapper>
  );
};

export default Map;
